import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useMatches, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { useTeamEvents } from "../api/events";
import { useListBrands } from "../api/generated/endpoints/teams/teams";
import type { TeamRole } from "../api/generated/model";
import { accessScopeQuery, findTeam, hasBrand } from "../api/session";
import { useMediaAccess } from "./media/access";
import { NotFoundScreen } from "./screens";
import { Shell } from "./shell/Shell";
import { StreamStatusContext } from "./stream-status";
import { useAccountTheme } from "./themes/account";
import { useProFrame } from "./ui-switch/frame";
import type { Ui } from "./ui-switch/places";

/** The account's theme, followed in a component of its own: a save under way re-renders nothing else. */
function AccountTheme({ teamId }: { teamId: string }) {
  useAccountTheme(teamId);
  return null;
}

interface FrameProps {
  teamId: string;
  role: TeamRole;
  userId: string;
  /** A page with no team of its own drawn in Pro's frame, under this brand, or Simple's. */
  pro?: { brandId: string; simple: boolean } | undefined;
}

function TeamFrame({ teamId, role, userId, pro }: FrameProps) {
  const streamStatus = useTeamEvents(teamId);
  // The team's frame and pages wait for its media cookies only while this tab has no word on
  // them yet, so their first reads already ask for each file's own URL (spec
  // assets/media-cookies, Design 4).
  const mediaReady = useMediaAccess(teamId, userId);
  // A page that draws its own frame (staticData.ownFrame) gets all of the above but no Shell.
  const ownFrame = useMatches({
    select: (matches) => matches.some((match) => match.staticData.ownFrame === true),
  });
  // Pro's frame is a feature's: the app gives it (spec ui-perf/admin-panel-pro-simple).
  const proFrame = useProFrame();
  if (!mediaReady) return null;
  const page = (
    <>
      {/* The account's theme, chosen on any device (spec web/appearance-tab), read after the grant. */}
      <AccountTheme teamId={teamId} />
      <StreamStatusContext value={streamStatus}>
        <Outlet />
      </StreamStatusContext>
    </>
  );
  if (ownFrame) return page;
  if (pro !== undefined && proFrame !== null) {
    return proFrame({ teamId, brandId: pro.brandId, simple: pro.simple, children: page });
  }
  return <Shell team={{ teamId, role, streamStatus }}>{page}</Shell>;
}

/**
 * Every team page: one event stream for the team, the shell with its sidebar, the team's media
 * cookies, and the matched route. The scope refetches on window focus, so a removal from the team
 * shows here without a reload (audit frontend 2.11).
 */
export function TeamLayout() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId" });
  const { data: scope } = useQuery(accessScopeQuery());
  // No scope means it is loading again, as after an ended session clears the cache: show nothing
  // rather than a not-found that is not true.
  if (scope === undefined) return null;
  const team = findTeam(scope, teamId);
  if (team === undefined) {
    return (
      <Shell>
        <NotFoundScreen />
      </Shell>
    );
  }
  // A frame per user and team: its media access reads the grant this tab kept when it mounts.
  return (
    <TeamFrame
      key={`${scope.user_id}:${teamId}`}
      teamId={teamId}
      role={team.role}
      userId={scope.user_id}
    />
  );
}

interface LastTeamProps {
  /** The team this tab was last on, and its brand there. */
  teamId: string | undefined;
  brandId: string | undefined;
  /** The UI the page is drawn in, by its `?ui=`. */
  ui: Ui;
}

/**
 * A page with no team of its own in a team's frame (`staticData.teamFrame`): the frame of
 * `teamId`, the team this tab was last on, while the user is still in it, else of their first
 * team, as `/` opens; the header alone when they are in no team, to the end of that navigation
 * even if they gain one. Pro's and Simple's frame is a brand's: that team's while it has a brand,
 * else the first team that has one; `brandId`, while the team has it, else the team's first, as
 * Pro's way in opens. With no brand in any team, the page keeps Classic's frame (spec
 * ui-perf/admin-panel-pro-simple).
 */
export function LastTeamFrame({ teamId, brandId, ui }: LastTeamProps) {
  const { data: scope } = useQuery(accessScopeQuery());
  // A navigation on which the user was in no team keeps the header alone, though the scope gains
  // a team during it (their first, made in the Admin Panel). A team's frame would mount the page
  // again, and the page would lose what it shows, such as an email's resend warning. Their next
  // navigation has the team's frame (spec ui-perf/staff-no-team-admin-panel).
  const entry = useLocation({ select: (location) => location.state.__TSR_key ?? location.state.key });
  const [bareOn, setBareOn] = useState<string>();
  const bare = scope?.teams.length === 0 ? entry : bareOn === entry ? bareOn : undefined;
  if (bare !== bareOn) setBareOn(bare);
  const last =
    scope === undefined || bare !== undefined
      ? undefined
      : ((teamId === undefined ? undefined : findTeam(scope, teamId)) ?? scope.teams[0]);
  const team =
    ui === "classic" || last === undefined || last.brand_ids.length > 0
      ? last
      : (scope?.teams.find((each) => each.brand_ids.length > 0) ?? last);
  const branded = ui !== "classic" && team !== undefined && team.brand_ids.length > 0;
  const kept = team !== undefined && brandId !== undefined && hasBrand(team, brandId) ? brandId : undefined;
  // The team's first brand as its list orders them (the scope's are by age), read only when needed.
  const first = useListBrands(team?.team_id ?? "", {
    query: { enabled: branded && kept === undefined, select: (brands) => brands[0]?.id },
  });
  if (scope === undefined) return null;
  if (team === undefined) {
    return (
      <Shell>
        <Outlet />
      </Shell>
    );
  }
  const proBrand = branded ? (kept ?? first.data) : undefined;
  // Pro's frame waits for the list rather than show Classic's first.
  if (branded && proBrand === undefined && first.isPending) return null;
  return (
    <TeamFrame
      key={`${scope.user_id}:${team.team_id}`}
      teamId={team.team_id}
      role={team.role}
      userId={scope.user_id}
      pro={proBrand === undefined ? undefined : { brandId: proBrand, simple: ui === "simple" }}
    />
  );
}
